import {useContext} from 'react';
import {Context} from '../context';
import {useGetLineItem} from './useGetLineItem';

const CART_LINES_REMOVE_MUTATION = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        id
        checkoutUrl
        totalQuantity
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  product {
                    id
                    title
                    handle
                  }
                  image {
                    url
                    altText
                  }
                  price {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export function useRemoveItemsFromCart() {
  const {client, cart, setCart, isInitializing} = useContext(Context);
  const getLineItem = useGetLineItem();

  async function removeItemsFromCart(variantIds) {
    if (client == null) {
      throw new Error('Shopify client not initialized');
    }
    
    if (cart == null || isInitializing) {
      throw new Error('Cart is not ready yet. Please wait for cart to initialize.');
    }

    if (variantIds.length < 1) {
      throw new Error('Must include at least one item to remove');
    }

    const lineItemIds = variantIds.map((variantId) => {
      const lineItem = getLineItem(variantId);
      if (lineItem === null) {
        throw new Error(
          `Could not find line item in cart with variant id: ${variantId}`,
        );
      }
      return String(lineItem.id);
    });

    try {
      const response = await client.request(CART_LINES_REMOVE_MUTATION, {
        variables: {
          cartId: cart.id,
          lineIds: lineItemIds
        }
      });

      if (response?.data?.cartLinesRemove?.userErrors?.length > 0) {
        throw new Error(response.data.cartLinesRemove.userErrors[0].message);
      }

      if (response?.data?.cartLinesRemove?.cart) {
        const updatedCart = {
          id: response.data.cartLinesRemove.cart.id,
          checkoutUrl: response.data.cartLinesRemove.cart.checkoutUrl,
          totalQuantity: response.data.cartLinesRemove.cart.totalQuantity || 0,
          lines: response.data.cartLinesRemove.cart.lines?.edges?.map(edge => edge.node) || [],
          cost: response.data.cartLinesRemove.cart.cost
        };
        setCart(updatedCart);
      }
    } catch (error) {
      console.error('Error removing items from cart:', error);
      throw error;
    }
  }

  return removeItemsFromCart;
}